import { useCallback, useState } from 'react'
import { Dialog, Flex, VisuallyHidden } from '@radix-ui/themes'
import { Button } from '@/components/subscription/button'
import { styles } from './styles.css'

type ConfirmOptions = {
  confirmLabel?: string
  cancelLabel?: string
}

type Props = {
  message: string
  onConfirm: () => void
  onCancel: () => void
} & ConfirmOptions

export const ConfirmationModal: React.FC<Props> = ({ message, confirmLabel = 'OK', cancelLabel, onConfirm, onCancel }) => (
  <Dialog.Root open onOpenChange={(isOpen) => !isOpen && onCancel()}>
    <Dialog.Content maxWidth="480px" aria-describedby={undefined}>
      <VisuallyHidden>
        <Dialog.Title>確認</Dialog.Title>
      </VisuallyHidden>
      <p className={styles.message}>{message}</p>
      <Flex direction="row" justify="end" gap="8px" pt="16px">
        {cancelLabel && (
          <Button as="button" size="s" color="bordered" onClick={onCancel}>
            {cancelLabel}
          </Button>
        )}
        <Button as="button" size="s" onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </Flex>
    </Dialog.Content>
  </Dialog.Root>
)

type ModalState = {
  message: string
  options: ConfirmOptions
  resolve: (result: boolean) => void
}

// window.confirm の代替として使える確認用のモーダル
//
//   const { confirm, confirmationModal } = useConfirmationModal()
//
//   const handleCancel = async () => {
//     if (!(await confirm('キャンセルしますか？'))) return
//     ...
//   }
//
//   return <>{confirmationModal} ...</>
export const useConfirmationModal = () => {
  const [state, setState] = useState<ModalState | null>(null)

  const confirm = useCallback(
    (message: string, options: ConfirmOptions = {}) =>
      new Promise<boolean>((resolve) => {
        setState((prev) => {
          prev?.resolve(false) // 表示中に再度呼ばれた場合、前のconfirmはキャンセル扱いにする

          return { message, options, resolve }
        })
      }),
    []
  )

  const close = (result: boolean) => {
    state?.resolve(result)
    setState(null)
  }

  const confirmationModal = state && (
    <ConfirmationModal
      message={state.message}
      confirmLabel={state.options.confirmLabel}
      cancelLabel={state.options.cancelLabel}
      onConfirm={() => close(true)}
      onCancel={() => close(false)}
    />
  )

  return { confirm, confirmationModal }
}
